import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Add16Regular,
  ArrowDown12Regular,
  ArrowDownLeft16Regular,
  ArrowUp12Regular,
  ArrowUpRight16Regular,
  Bed16Regular,
  Call16Filled,
  CallEnd16Regular,
  CallMissed16Regular,
  Checkmark12Regular,
  Copy16Regular,
  DataBarVertical16Regular,
  Delete16Regular,
  Dismiss12Regular,
  Filter16Regular,
  Headset16Regular,
  Home16Regular,
  MoreHorizontal16Regular,
  Pause16Filled,
  Person16Regular,
  PersonAdd16Regular,
  Play16Filled,
  Receipt16Regular,
  Search16Regular,
  Sparkle16Regular,
} from "@fluentui/react-icons";
import type { FluentIcon } from "@fluentui/react-icons";
import styles from "./CallLogs.module.css";
import type { ApiOptions } from "../dashboardApi";
import {
  canDial,
  canListen,
  createContact,
  dialNumber,
  fetchRecordingAudio,
  getCallLog,
  hangupCall,
  hideCallLog,
  listContacts,
  listenIn,
} from "../frontdeskApi";
import type { CallLogResult, CallLogRowDto, ContactDto, DialDefaults, LogSort, LogTab, LogType } from "../frontdeskApi";

/* ------------------------------------------------------------------ *
 *  Call Logs tab (white theme) – lives inside the Front Desk modal.
 *  Tabs (All / Missed / Ongoing / Recorded) · filter · make a call ·
 *  sortable table with live ongoing calls, listen-in, recordings and a
 *  per-row menu. Fills its parent (the 900 × 485 Frontdesk body area).
 * ------------------------------------------------------------------ */

type CallType = LogType;
type TabKey = LogTab;
type SortKey = LogSort;

/** One row of the list: the backend row with its time as milliseconds. */
interface Log {
  id: string;
  name: string | null; // null = unknown number
  number: string;
  type: CallType;
  agent: string | null;
  inbox: string | null; // queue name
  queueId: string | null;
  startedAt: number; // ms
  seconds: number; // finished calls
  recorded: boolean;
  recordingId: string | null;
}

export interface CallLogsProps {
  /** Extra search text (e.g. from the page header search). */
  query?: string | undefined;
  /** Gateway and sign-in (same object the dashboard uses). */
  api?: ApiOptions;
  /** Number and agent used by "Make a call" and "Call back"; supervisor used by "Listen in". */
  dial?: DialDefaults;
}

/* ---------- reference data ---------- */

/** Icon and colours for the inbox (queue) tiles. Queues that are not listed get the generic look. */
const INBOX_LOOK: Record<string, { Icon: FluentIcon; tone: string }> = {
  reservations: { Icon: Bed16Regular, tone: "#e4eeff|#2f6fe0" },
  concierge: { Icon: Sparkle16Regular, tone: "#efe8ff|#7a4fd6" },
  housekeeping: { Icon: Home16Regular, tone: "#def7e6|#1c8a4a" },
  billing: { Icon: Receipt16Regular, tone: "#fff0d9|#c77a0a" },
};
const GENERIC_LOOK = { Icon: Headset16Regular, tone: "#eceff4|#4b5565" };
const lookOf = (name: string | null) => INBOX_LOOK[(name ?? "").trim().toLowerCase()] ?? GENERIC_LOOK;

const NO_API: ApiOptions = {};
const PAGE_STEP = 20;
const FIRST_PAGE = 24;
const REFRESH_MS = 15000;

const TYPE_LABEL: Record<CallType, string> = { incoming: "Incoming", outgoing: "Outgoing", missed: "Missed", rejected: "Rejected", ongoing: "Ongoing" };
const TYPE_FILTERS: CallType[] = ["incoming", "outgoing", "missed", "rejected", "ongoing"];
const TYPE_ICON: Record<CallType, FluentIcon> = {
  incoming: ArrowDownLeft16Regular,
  outgoing: ArrowUpRight16Regular,
  missed: CallMissed16Regular,
  rejected: CallEnd16Regular,
  ongoing: DataBarVertical16Regular,
};
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/* ---------- helpers ---------- */

const hash = (s: string) => {
  let h = 7;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return h;
};
const pad = (n: number) => (n < 10 ? "0" + n : String(n));
const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();

const fmtDuration = (s: number) => {
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  return h > 0 ? `${h}:${pad(m)}:${pad(sec)}` : `${m}:${pad(sec)}`;
};
const fmtDate = (ms: number, now: number) => {
  const d = new Date(ms);
  const h = d.getHours();
  const time = `${h % 12 === 0 ? 12 : h % 12}:${pad(d.getMinutes())} ${h < 12 ? "AM" : "PM"}`;
  const diff = Math.round((startOfDay(new Date(now)) - startOfDay(d)) / 86400000);
  if (diff === 0) return `Today, ${time}`;
  if (diff === 1) return `Yesterday, ${time}`;
  return `${MONTHS[d.getMonth()]} ${d.getDate()}, ${time}`;
};

const toLog = (r: CallLogRowDto): Log => ({
  id: r.id,
  name: r.name,
  number: r.number,
  type: r.type,
  agent: r.agent,
  inbox: r.inbox,
  queueId: r.queueId,
  startedAt: Date.parse(r.startedAt),
  seconds: r.seconds,
  recorded: r.recorded,
  recordingId: r.recordingId,
});

const Avatar: React.FC<{ name: string | null; size: number }> = ({ name, size }) => {
  if (!name) {
    return (
      <span className={`${styles.avatar} ${styles.avatarUnknown}`} style={{ width: size, height: size }} aria-hidden="true">
        <Person16Regular style={{ width: size * 0.62, height: size * 0.62 }} />
      </span>
    );
  }
  const h = hash(name) % 360;
  const initials = name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p.charAt(0))
    .join("")
    .toUpperCase();
  return (
    <span className={styles.avatar} style={{ width: size, height: size, fontSize: size * 0.4, background: `linear-gradient(135deg, hsl(${h} 72% 64%), hsl(${(h + 35) % 360} 68% 50%))` }} aria-hidden="true">
      {initials}
    </span>
  );
};

/* ---------- main component ---------- */

type PopId = "filter" | `row:${string}`;

const CallLogs: React.FC<CallLogsProps> = ({ query, api, dial }) => {
  const apiOpts = api ?? NO_API;
  const [logs, setLogs] = useState<Log[]>([]);
  const [total, setTotal] = useState(0);
  const [tabCounts, setTabCounts] = useState({ all: 0, missed: 0, ongoing: 0, recorded: 0 });
  const [agentList, setAgentList] = useState<{ id: string; name: string }[]>([]);
  const [queueList, setQueueList] = useState<{ id: string; name: string }[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const skew = useRef(0); // server clock minus browser clock, so live durations match the server
  const [now, setNow] = useState(() => Date.now());
  const [tab, setTab] = useState<TabKey>("all");
  const [typeF, setTypeF] = useState<CallType | "all">("all");
  const [inboxF, setInboxF] = useState("all"); // queue id
  const [agentF, setAgentF] = useState("all"); // agent id
  const [sort, setSort] = useState<{ key: SortKey; dir: "asc" | "desc" }>({ key: "date", dir: "desc" });
  const [limit, setLimit] = useState(FIRST_PAGE);
  const [pop, setPop] = useState<PopId | null>(null);
  const [dropUp, setDropUp] = useState(false);
  const [calling, setCalling] = useState(false);
  const [listening, setListening] = useState<string | null>(null);
  const [playing, setPlaying] = useState<string | null>(null);
  const [toast, setToast] = useState<{ msg: string; id: number } | null>(null);
  const toastTimer = useRef<number | undefined>(undefined);
  const listRef = useRef<HTMLDivElement>(null);
  const audioRef = useRef<{ el: HTMLAudioElement; url: string } | null>(null);

  const search = (query ?? "").trim();

  /* the list comes from the backend: filters, sort and paging are answered there */
  useEffect(() => {
    const ac = new AbortController();
    const load = (first: boolean) => {
      if (first) setLoading(true);
      getCallLog(
        apiOpts,
        {
          tab,
          type: typeF === "all" ? undefined : typeF,
          queueId: inboxF === "all" ? undefined : inboxF,
          agentId: agentF === "all" ? undefined : agentF,
          search,
          sort: sort.key,
          desc: sort.dir === "desc",
          page: 1,
          pageSize: limit,
        },
        ac.signal,
      )
        .then((r: CallLogResult) => {
          if (ac.signal.aborted) return;
          skew.current = Date.parse(r.serverTime) - Date.now();
          setLogs(r.items.map(toLog));
          setTotal(r.total);
          setTabCounts(r.counts);
          setAgentList(r.agents);
          setQueueList(r.queues);
          setLoadError(null);
        })
        .catch((e: unknown) => {
          if (ac.signal.aborted) return;
          setLoadError(e instanceof Error ? e.message : "Could not load the call log.");
        })
        .finally(() => {
          if (!ac.signal.aborted) setLoading(false);
        });
    };
    const wait = window.setTimeout(() => load(true), search ? 250 : 0);
    const poll = window.setInterval(() => load(false), REFRESH_MS);
    return () => {
      ac.abort();
      window.clearTimeout(wait);
      window.clearInterval(poll);
    };
  }, [apiOpts, tab, typeF, inboxF, agentF, search, sort, limit, reloadKey]);

  const hasOngoing = logs.some((l) => l.type === "ongoing");
  useEffect(() => {
    if (!hasOngoing) return;
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [hasOngoing]);
  const stopAudio = useCallback(() => {
    const a = audioRef.current;
    if (a) {
      a.el.pause();
      URL.revokeObjectURL(a.url);
      audioRef.current = null;
    }
  }, []);
  useEffect(
    () => () => {
      window.clearTimeout(toastTimer.current);
      stopAudio();
    },
    [stopAudio],
  );

  const showToast = useCallback((msg: string) => {
    window.clearTimeout(toastTimer.current);
    setToast({ msg, id: Date.now() });
    toastTimer.current = window.setTimeout(() => setToast(null), 2200);
  }, []);
  const fail = useCallback((e: unknown) => showToast(e instanceof Error ? e.message : "Something went wrong."), [showToast]);
  const refresh = useCallback(() => setReloadKey((k) => k + 1), []);

  /* close popovers on outside pointer-down */
  useEffect(() => {
    if (!pop) return;
    const h = (e: PointerEvent) => {
      const el = e.target as HTMLElement | null;
      if (!el || !el.closest(`[data-pop="${pop}"]`)) setPop(null);
    };
    document.addEventListener("pointerdown", h);
    return () => document.removeEventListener("pointerdown", h);
  }, [pop]);

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key !== "Escape") return;
    if (calling || pop) {
      e.stopPropagation();
      e.nativeEvent.stopPropagation();
      if (calling) setCalling(false);
      else setPop(null);
    }
  };

  /* ----- derived data ----- */
  const duration = useCallback(
    (l: Log) => (l.type === "ongoing" ? Math.max(0, Math.floor((now + skew.current - l.startedAt) / 1000)) : l.seconds),
    [now],
  );

  const shown = logs;
  const activeFilters = (typeF !== "all" ? 1 : 0) + (inboxF !== "all" ? 1 : 0) + (agentF !== "all" ? 1 : 0);

  /* ----- actions ----- */
  const toggleSort = (key: SortKey) => {
    setSort((s) => (s.key === key ? { key, dir: s.dir === "asc" ? "desc" : "asc" } : { key, dir: key === "name" ? "asc" : "desc" }));
    setLimit(FIRST_PAGE);
  };

  const startCall = (name: string | null, number: string, queueId: string | null) => {
    if (!canDial(dial)) {
      window.location.href = `tel:${number.replace(/[^+\d]/g, "")}`;
      showToast(`Calling ${name ?? number}…`);
      return;
    }
    dialNumber(apiOpts, dial, { to: number, queueId: queueId ?? undefined })
      .then(() => {
        setTab("all");
        setSort({ key: "date", dir: "desc" });
        setNow(Date.now());
        refresh();
        showToast(`Calling ${name ?? number}…`);
      })
      .catch(fail);
  };
  const endCall = (l: Log) => {
    hangupCall(apiOpts, l.id)
      .then(() => {
        if (listening === l.id) setListening(null);
        setPop(null);
        showToast(`Call ended · ${fmtDuration(duration(l))}`);
        window.setTimeout(refresh, 800);
      })
      .catch(fail);
  };
  const copyNumber = (l: Log) => {
    try {
      const p = navigator.clipboard ? navigator.clipboard.writeText(l.number) : undefined;
      if (p) p.catch(() => undefined);
    } catch {
      /* clipboard may be unavailable */
    }
    setPop(null);
    showToast("Number copied");
  };
  const toggleListen = (l: Log) => {
    if (listening === l.id) {
      setListening(null);
      showToast("Hang up your phone to stop listening");
      return;
    }
    if (!canListen(dial)) {
      showToast("Listening in is not set up for your account");
      return;
    }
    listenIn(apiOpts, dial, l.id)
      .then(() => {
        setListening(l.id);
        showToast(`Listening in on ${l.name ?? l.number}`);
      })
      .catch(fail);
  };
  const togglePlay = (l: Log) => {
    if (playing === l.id) {
      stopAudio();
      setPlaying(null);
      return;
    }
    if (!l.recordingId) {
      showToast("This recording has no audio file yet");
      return;
    }
    stopAudio();
    setPlaying(l.id);
    fetchRecordingAudio(apiOpts, l.recordingId)
      .then((url) => {
        const el = new Audio(url);
        audioRef.current = { el, url };
        el.onended = () => {
          stopAudio();
          setPlaying((p) => (p === l.id ? null : p));
        };
        showToast(`Playing recording · ${l.name ?? l.number}`);
        return el.play();
      })
      .catch((e: unknown) => {
        stopAudio();
        setPlaying((p) => (p === l.id ? null : p));
        fail(e);
      });
  };
  const saveToContacts = (l: Log) => {
    setPop(null);
    createContact(apiOpts, { name: l.number, role: "Contact", phone: l.number, source: "Device" })
      .then(() => showToast("Saved to contacts"))
      .catch(fail);
  };
  const deleteLog = (l: Log) => {
    hideCallLog(apiOpts, l.id)
      .then(() => {
        setLogs((ls) => ls.filter((x) => x.id !== l.id));
        setTotal((n) => Math.max(0, n - 1));
        if (playing === l.id) {
          stopAudio();
          setPlaying(null);
        }
        if (listening === l.id) setListening(null);
        setPop(null);
        showToast("Call log deleted");
        refresh();
      })
      .catch((e: unknown) => {
        setPop(null);
        showToast(l.type === "ongoing" ? "End the call before deleting its log" : e instanceof Error ? e.message : "Could not delete the log.");
      });
  };
  const openMenu = (id: string, btn: HTMLElement) => {
    const list = listRef.current;
    if (list) {
      const r = btn.getBoundingClientRect();
      const c = list.getBoundingClientRect();
      setDropUp((c.bottom - r.bottom) / c.height < 0.3);
    }
    setPop((p) => (p === `row:${id}` ? null : `row:${id}`));
  };

  const SortHead: React.FC<{ k: SortKey; label: string }> = ({ k, label }) => {
    const on = sort.key === k;
    return (
      <button type="button" className={`${styles.th} ${on ? styles.thOn : ""}`} aria-sort={on ? (sort.dir === "asc" ? "ascending" : "descending") : "none"} onClick={() => toggleSort(k)}>
        {label}
        {on && (sort.dir === "asc" ? <ArrowUp12Regular /> : <ArrowDown12Regular />)}
      </button>
    );
  };

  const tabs: [TabKey, string][] = [
    ["all", "All"],
    ["missed", "Missed"],
    ["ongoing", "Ongoing"],
    ["recorded", "Recorded"],
  ];

  return (
    <div className={styles.root} tabIndex={-1} onKeyDown={onKeyDown}>
      <section className={styles.panel} aria-label="Call logs">
        {/* ───────── toolbar ───────── */}
        <div className={styles.bar}>
          <div className={styles.tabs} role="tablist" aria-label="Call filter">
            {tabs.map(([key, label]) => (
              <button
                key={key}
                type="button"
                role="tab"
                aria-selected={tab === key}
                className={`${styles.tab} ${tab === key ? styles.tabOn : ""}`}
                onClick={() => {
                  setTab(key);
                  setLimit(FIRST_PAGE);
                }}
              >
                {label}
                <span className={styles.tabCount}>{tabCounts[key]}</span>
              </button>
            ))}
          </div>

          <div className={styles.actions}>
            <div className={styles.anchor} data-pop="filter">
              <button type="button" className={`${styles.btn} ${activeFilters ? styles.btnOn : ""}`} aria-haspopup="dialog" aria-expanded={pop === "filter"} onClick={() => setPop((p) => (p === "filter" ? null : "filter"))}>
                <Filter16Regular />
                Filter
                {activeFilters > 0 && <span className={styles.badge}>{activeFilters}</span>}
              </button>
              {pop === "filter" && (
                <div className={`${styles.pop} ${styles.filterPop}`} role="dialog" aria-label="Filter calls">
                  <div className={styles.fLabel}>TYPE</div>
                  <div className={styles.chips}>
                    {(["all", ...TYPE_FILTERS] as (CallType | "all")[]).map((t) => (
                      <button key={t} type="button" aria-pressed={typeF === t} className={`${styles.chip} ${typeF === t ? styles.chipOn : ""}`} onClick={() => { setTypeF(t); setLimit(FIRST_PAGE); }}>
                        {t === "all" ? "All" : TYPE_LABEL[t]}
                      </button>
                    ))}
                  </div>
                  <div className={styles.fLabel}>INBOX</div>
                  <div className={styles.chips}>
                    {[{ id: "all", name: "All" }, ...queueList].map((k) => (
                      <button key={k.id} type="button" aria-pressed={inboxF === k.id} className={`${styles.chip} ${inboxF === k.id ? styles.chipOn : ""}`} onClick={() => { setInboxF(k.id); setLimit(FIRST_PAGE); }}>
                        {k.name}
                      </button>
                    ))}
                  </div>
                  <label className={styles.fLabel} htmlFor="cl-agent">
                    AGENT
                  </label>
                  <select id="cl-agent" className={styles.select} value={agentF} onChange={(e) => { setAgentF(e.target.value); setLimit(FIRST_PAGE); }}>
                    <option value="all">All agents</option>
                    {agentList.map((a) => (
                      <option key={a.id} value={a.id}>
                        {a.name}
                      </option>
                    ))}
                  </select>
                  <div className={styles.fFoot}>
                    <button
                      type="button"
                      className={styles.linkBtn}
                      onClick={() => {
                        setTypeF("all");
                        setInboxF("all");
                        setAgentF("all");
                      }}
                    >
                      Clear all
                    </button>
                    <button type="button" className={styles.btnPrimarySm} onClick={() => setPop(null)}>
                      Done
                    </button>
                  </div>
                </div>
              )}
            </div>
            <button type="button" className={styles.btnPrimary} aria-haspopup="dialog" onClick={() => setCalling(true)}>
              <Add16Regular />
              Make a call
            </button>
          </div>
        </div>

        {/* ───────── table ───────── */}
        <div className={styles.head} role="row">
          <SortHead k="name" label="Contacts" />
          <span className={styles.thStatic}>Type</span>
          <span className={styles.thStatic}>Users</span>
          <span className={styles.thStatic}>Inbox</span>
          <SortHead k="duration" label="Duration" />
          <SortHead k="date" label="Date" />
          <span />
        </div>

        <div className={styles.list} ref={listRef} role="table" aria-label="Call log entries">
          {shown.length === 0 && loading && <div className={styles.empty}>Loading calls…</div>}
          {shown.length === 0 && !loading && loadError && (
            <div className={styles.empty}>
              {loadError}{" "}
              <button type="button" className={styles.btn} onClick={refresh}>
                Try again
              </button>
            </div>
          )}
          {shown.length === 0 && !loading && !loadError && <div className={styles.empty}>No calls match these filters</div>}
          {shown.map((l) => {
            const TypeIcon = TYPE_ICON[l.type];
            const ib = lookOf(l.inbox);
            const [tbg, tfg] = ib.tone.split("|");
            const isOngoing = l.type === "ongoing";
            const popId: PopId = `row:${l.id}`;
            const secs = duration(l);
            const isPlaying = playing === l.id;
            const isListening = listening === l.id;
            return (
              <div key={l.id} className={`${styles.row} ${pop === popId ? styles.rowOpen : ""}`} role="row">
                <div className={styles.cellName}>
                  <Avatar name={l.name} size={18} />
                  <span className={styles.nameText}>{l.name ?? l.number}</span>
                </div>

                <div className={`${styles.cellType} ${styles["type_" + l.type]}`}>
                  {isOngoing ? (
                    <>
                      <span className={styles.ongoingLabel}>
                        <TypeIcon />
                        Ongoing
                      </span>
                      <span className={styles.listenWrap}>
                        <button
                          type="button"
                          className={`${styles.listenBtn} ${isListening ? styles.listenOn : ""}`}
                          aria-pressed={isListening}
                          onClick={() => toggleListen(l)}
                        >
                          <Headset16Regular />
                          {isListening ? "Listening" : "Listen in"}
                        </button>
                        <span className={styles.tip} role="tooltip">
                          {isListening ? "Stop listening" : "Listen in on this call"}
                        </span>
                      </span>
                    </>
                  ) : (
                    <>
                      <TypeIcon />
                      {TYPE_LABEL[l.type]}
                    </>
                  )}
                </div>

                <div className={styles.cellUser}>
                  {l.agent ? (
                    <>
                      <Avatar name={l.agent} size={14} />
                      <span>{l.agent}</span>
                    </>
                  ) : (
                    <span className={styles.dash}>-</span>
                  )}
                </div>

                <div className={styles.cellInbox}>
                  <span className={styles.inboxTile} style={{ background: tbg ?? "#eee", color: tfg ?? "#333" }}>
                    <ib.Icon />
                  </span>
                  <span>{l.inbox ?? "-"}</span>
                </div>

                <div className={styles.cellDur}>
                  {isOngoing ? (
                    <>
                      <span className={`${styles.play} ${styles.playLive}`} aria-hidden="true">
                        <Play16Filled />
                      </span>
                      <span className={styles.durLive}>{fmtDuration(secs)}</span>
                    </>
                  ) : l.seconds > 0 ? (
                    <>
                      {l.recorded ? (
                        <button
                          type="button"
                          className={`${styles.play} ${styles.playBtn} ${isPlaying ? styles.playOn : ""}`}
                          aria-label={isPlaying ? "Pause recording" : "Play recording"}
                          aria-pressed={isPlaying}
                          onClick={() => togglePlay(l)}
                        >
                          {isPlaying ? <Pause16Filled /> : <Play16Filled />}
                        </button>
                      ) : (
                        <span className={`${styles.play} ${styles.playOff}`} aria-hidden="true">
                          <Play16Filled />
                        </span>
                      )}
                      <span>{fmtDuration(secs)}</span>
                    </>
                  ) : (
                    <span className={styles.dash}>-</span>
                  )}
                </div>

                <div className={styles.cellDate}>{fmtDate(l.startedAt, now + skew.current)}</div>

                <div className={styles.cellMenu} data-pop={popId}>
                  <button type="button" className={styles.dots} aria-haspopup="menu" aria-expanded={pop === popId} aria-label={`Options for ${l.name ?? l.number}`} onClick={(e) => openMenu(l.id, e.currentTarget)}>
                    <MoreHorizontal16Regular />
                  </button>
                  {pop === popId && (
                    <div className={`${styles.pop} ${styles.menuPop} ${dropUp ? styles.menuUp : ""}`} role="menu">
                      {isOngoing ? (
                        <button type="button" role="menuitem" className={`${styles.popItem} ${styles.danger}`} onClick={() => endCall(l)}>
                          <CallEnd16Regular /> End call
                        </button>
                      ) : (
                        <button
                          type="button"
                          role="menuitem"
                          className={styles.popItem}
                          onClick={() => {
                            setPop(null);
                            startCall(l.name, l.number, l.queueId);
                          }}
                        >
                          <Call16Filled /> Call back
                        </button>
                      )}
                      <button type="button" role="menuitem" className={styles.popItem} onClick={() => copyNumber(l)}>
                        <Copy16Regular /> Copy number
                      </button>
                      {!l.name && (
                        <button
                          type="button"
                          role="menuitem"
                          className={styles.popItem}
                          onClick={() => saveToContacts(l)}
                        >
                          <PersonAdd16Regular /> Add to contacts
                        </button>
                      )}
                      <button
                        type="button"
                        role="menuitem"
                        className={`${styles.popItem} ${styles.danger}`}
                        onClick={() => deleteLog(l)}
                      >
                        <Delete16Regular /> Delete log
                      </button>
                    </div>
                  )}
                </div>
              </div>
            );
          })}

          {total > shown.length && (
            <div className={styles.more}>
              <span>
                Showing {shown.length} of {total}
              </span>
              <button type="button" className={styles.btn} onClick={() => setLimit((n) => n + PAGE_STEP)}>
                Show more
              </button>
            </div>
          )}
        </div>
      </section>

      {calling && (
        <MakeCall
          api={apiOpts}
          queues={queueList}
          onCancel={() => setCalling(false)}
          onCall={(name, number, queueId) => {
            setCalling(false);
            startCall(name, number, queueId);
          }}
        />
      )}

      {toast && (
        <div key={toast.id} className={styles.toast} role="status">
          {toast.msg}
        </div>
      )}
    </div>
  );
};

/* ---------- make-a-call dialog ---------- */

const MakeCall: React.FC<{
  api: ApiOptions;
  queues: { id: string; name: string }[];
  onCancel: () => void;
  onCall: (name: string | null, number: string, queueId: string | null) => void;
}> = ({ api, queues, onCancel, onCall }) => {
  const [value, setValue] = useState("");
  const [inbox, setInbox] = useState<string | null>((queues.find((x) => x.name.toLowerCase() === "reservations") ?? queues[0])?.id ?? null);
  const [picked, setPicked] = useState<ContactDto | null>(null);
  const [people, setPeople] = useState<ContactDto[]>([]);

  useEffect(() => {
    const ac = new AbortController();
    listContacts(api, ac.signal)
      .then((cs) => {
        if (!ac.signal.aborted) setPeople(cs);
      })
      .catch(() => undefined);
    return () => ac.abort();
  }, [api]);

  const q = value.trim().toLowerCase();
  const matches = q && !picked ? people.filter((p) => p.name.toLowerCase().indexOf(q) !== -1 && p.phones.length > 0).slice(0, 4) : [];
  const isNumber = /^[+0-9][0-9 ()+-]{5,}$/.test(value.trim());
  const ok = picked !== null || isNumber;

  const submit = () => {
    if (!ok) return;
    if (picked) onCall(picked.name, picked.phones[0]?.value ?? "", inbox);
    else onCall(null, value.trim(), inbox);
  };

  return (
    <div
      className={styles.dialogWrap}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onCancel();
      }}
    >
      <form
        className={styles.dialog}
        role="dialog"
        aria-modal="true"
        aria-label="Make a call"
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
      >
        <div className={styles.dHead}>
          <h3>Make a call</h3>
          <button type="button" className={styles.dots} aria-label="Close" onClick={onCancel}>
            <Dismiss12Regular />
          </button>
        </div>

        <label className={styles.field}>
          <span className={styles.fLabel}>CONTACT OR NUMBER</span>
          <span className={styles.inputWrap}>
            <Search16Regular />
            <input
              value={value}
              autoFocus
              placeholder="Search a contact or type +234…"
              onChange={(e) => {
                setValue(e.target.value);
                setPicked(null);
              }}
            />
            {picked && (
              <span className={styles.pickedTag}>
                <Checkmark12Regular /> contact
              </span>
            )}
          </span>
        </label>
        {matches.length > 0 && (
          <div className={styles.suggest} role="listbox" aria-label="Contacts">
            {matches.map((m) => (
              <button
                key={m.id}
                type="button"
                role="option"
                aria-selected={false}
                className={styles.suggestItem}
                onClick={() => {
                  setPicked(m);
                  setValue(m.name);
                }}
              >
                <Avatar name={m.name} size={14} />
                {m.name}
              </button>
            ))}
          </div>
        )}

        <div className={styles.field}>
          <span className={styles.fLabel}>CALL FROM INBOX</span>
          <div className={styles.chips}>
            {queues.map((i) => (
              <button key={i.id} type="button" aria-pressed={inbox === i.id} className={`${styles.chip} ${inbox === i.id ? styles.chipOn : ""}`} onClick={() => setInbox(i.id)}>
                {i.name}
              </button>
            ))}
          </div>
        </div>

        <div className={styles.dFoot}>
          <button type="button" className={styles.btn} onClick={onCancel}>
            Cancel
          </button>
          <button type="submit" className={styles.btnPrimary} disabled={!ok}>
            <Call16Filled />
            Call
          </button>
        </div>
      </form>
    </div>
  );
};

export default CallLogs;